import { useMemo, useState } from 'react'

import { Typography, Button, Card, Box, Switch, Breadcrumbs } from '@mui/material'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  Wifi,
  Battery,
  Download,
  Bell,
  Tag,
  MapPin,
  TrendingUp,
  IdCard,
  Settings,
  Activity,
  ExternalLink,
  Power,
  ArrowLeft,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  SignalBars,
  StatusBadge,
  SectionHeader,
  InfoRow,
  LegendItem,
} from '../../../common/components'
import { formatDateOnly } from '../../../common/functions/timeConversion'
import { diffFromNow, formatDateTime, useTimezone } from '../../../utils/datetime'
import { filterDeviceDatalogsApi, getAlarmProfileByUuid } from '../Devices/api'
import AssetTagHistoryTable from '../Devices/AssetTagHistoryTable'

import { getInputModuleByUuidApi } from './api'

// 24h trend chart renders from the same `/device-datalogs/filter` source
// the device detail page uses — the dummy `sensor` field on the module
// payload maps straight into the chart's sensor list, so the trend
// appears as soon as the backend serves real datalogs.

const ValidityCell = <StatusBadge color="green" dot value="Valid · 198 days" />

// Per-key label/unit overrides for `inputModule.lastLogData`. Mirrors
// DeviceDetail.jsx's CURRENT_READING_META so the Input Module card
// reads identically to the device detail page. Unknown keys fall
// through to a generic label with no unit so the layout still works
// for fields the backend hasn't whitelisted.
const CURRENT_READING_META = {
  temperature: { label: 'Temperature', unit: '°C' },
  humidity: { label: 'Rel. Humidity', unit: '%' },
  dewpoint: { label: 'Dewpoint', unit: '°C' },
  signal: { label: 'Signal', unit: 'dBm' },
}

// Map `inputModule.lastLogData` to `{ key, label, value, unit }` rows.
// Numeric values render as-is; non-numeric values fall back to the raw
// string so the card never drops a reading just because the backend
// shipped a string.
const buildCurrentReadings = (lastLogData) => {
  if (!lastLogData || typeof lastLogData !== 'object') return []
  return Object.entries(lastLogData).map(([key, value]) => {
    const meta = CURRENT_READING_META[key] || { label: key, unit: '' }
    return { key, label: meta.label, unit: meta.unit, value }
  })
}

const HEALTH_ROWS = [
  {
    label: 'Status',
    icon: <Power size={14} />,
    value: (
      <StatusBadge color="green" dot>
        Powered On
      </StatusBadge>
    ),
  },
  {
    label: 'Connectivity',
    icon: <Wifi size={14} />,
    value: <span>LoRaWAN · Online</span>,
  },
  {
    label: 'Battery',
    icon: <Battery size={14} />,
    value: (
      <div className="flex items-center gap-1">
        <Battery size={22} />
        --
      </div>
    ),
  },
  {
    label: 'Signal',
    icon: <Activity size={14} />,
    value: <SignalBars level={4} value={92} />,
  },
  { label: 'Last data', icon: <Activity size={14} />, value: <span>12 sec ago</span> },
]

// ── chart helpers ──────────────────────────────────────────────────────────
// Mirrors DeviceDetail.jsx verbatim so the Input Module 24-Hour Trend
// renders identically. Each sensor on the module becomes its own chart
// card; bands fold in from `alarmProfile` payloads returned by the
// datalogs endpoint, the same way the device page consumes them.
const CHART_W = 600
const CHART_H = 160
const GRID_Y = [20, 50, 80, 110, 140]

// Single brand green for every chart line — keeps the trend identity
// consistent across sensor types. Threshold colours (warn/crit) stay
// separate so the tolerance bands still read as amber/red.
const CHART_LINE_COLOR = '#2fbf71'
const SENSOR_PALETTE = {
  Temperature: { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
  'Rel. Humidity': { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
  Dewpoint: { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' },
}

// Fuzzy lookup by sensor name (case-insensitive substring). Used to map
// `module.sensor.name` to a palette entry when the API ships a name that
// doesn't exactly match the lookup keys.
const findKey = (name, table) => {
  if (!name) return undefined
  if (table[name]) return name
  const hit = Object.keys(table).find((k) => name.toLowerCase().includes(k.toLowerCase()))
  return hit
}

// Map raw `/device-datalogs/filter` rows to `{ time, v }` points for the
// SVG chart. Sorts ascending by `logDateTime` so the line draws left→right
// and formats the timestamp as HH:mm (24h). Falls back to an empty array
// when the API hasn't returned any rows yet — the chart will simply
// render its "No data found" empty state until the query resolves.
const mapDatalogsToSeries = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return []
  return [...rows]
    .sort((a, b) => new Date(a.logDateTime) - new Date(b.logDateTime))
    .map((row) => {
      const d = new Date(row.logDateTime)
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      const value = Number(row.sensorValue)
      const dateText = typeof formatDateOnly === 'function' ? formatDateOnly(row.logDateTime) : null
      return {
        time: `${hh}:${mm}`,
        date: dateText || row.logDateTime?.slice(0, 10) || '',
        v: Number.isFinite(value) ? value : null,
      }
    })
    .filter((p) => p.v !== null)
}

// Aggregate `alarmProfile[].profiles[]` into the four chart bands
// (warn upper/lower, crit upper/lower). Same shape as DeviceDetail so
// the dashed-line rendering stays identical across pages.
const bandsFromAlarmProfile = (profiles) => {
  if (!Array.isArray(profiles) || profiles.length === 0) {
    return { warnUpper: null, warnLower: null, critUpper: null, critLower: null }
  }
  const bySeverity = { WARNING: [], CRITICAL: [] }
  for (const p of profiles) {
    const sev = (p?.severity || '').toUpperCase()
    if (!bySeverity[sev]) continue
    const min = Number(p.minValue)
    const max = Number(p.maxValue)
    if (!Number.isFinite(min) || !Number.isFinite(max)) continue
    bySeverity[sev].push({ min, max })
  }
  const reduceBands = (bands) => {
    if (bands.length === 0) return { upper: null, lower: null }
    return {
      upper: Math.max(...bands.map((b) => b.max)),
      lower: Math.min(...bands.map((b) => b.min)),
    }
  }
  const warn = reduceBands(bySeverity.WARNING)
  const crit = reduceBands(bySeverity.CRITICAL)
  return {
    warnUpper: warn.upper,
    warnLower: warn.lower,
    critUpper: crit.upper,
    critLower: crit.lower,
  }
}

// Format a Y-axis tick value as a plain integer so labels stay short
// and easy to scan. NaN / undefined fall through to `--`.
const formatTickValue = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return '--'
  return Math.round(n).toString()
}

// Generic Y-axis mapper. Scales the series' [min, max] onto the SVG
// height so different sensors (°C, %, etc.) all fill the chart area.
// Every threshold from every configured band folds into the range so
// all dashed lines stay visible even when the data sits well outside
// the bands.
const buildScaler = (series, bands = []) => {
  const values = series.map((d) => d.v)
  const extras = []
  for (const band of bands) {
    for (const v of [band?.warnUpper, band?.warnLower, band?.critUpper, band?.critLower]) {
      if (v !== null && v !== undefined && Number.isFinite(Number(v))) extras.push(Number(v))
    }
  }
  const all = [...values, ...extras]
  const min = all.length ? Math.min(...all) : 0
  const max = all.length ? Math.max(...all) : 1
  const span = max - min || 1
  const toY = (v) => CHART_H - ((v - min) / span) * (CHART_H - 20) - 10
  const toValue = (y) => {
    if (!all.length) return min
    return min + ((CHART_H - y - 10) / (CHART_H - 20)) * span
  }
  return { toY, toValue, min, max }
}

// Build the chart descriptors for every sensor reported by the module.
// Same shape as DeviceDetail's `buildSensorCharts` so the JSX can reuse
// the same `<SensorChart>` component for both pages.
const buildSensorCharts = (sensors, datalogsBySensor = {}, alarmBandsBySensorId = {}) => {
  if (!Array.isArray(sensors) || sensors.length === 0) return []
  const priority = (name) => {
    const n = (name || '').toLowerCase()
    if (n.includes('temperature')) return 0
    if (n.includes('humidity')) return 1
    if (n.includes('dewpoint') || n.includes('dew point')) return 2
    return 3
  }
  const sortedSensors = [...sensors].sort((a, b) => priority(a.name) - priority(b.name))
  return sortedSensors.map((sensor, idx) => {
    const nameKey = findKey(sensor.name, SENSOR_PALETTE)
    const palette = nameKey
      ? SENSOR_PALETTE[nameKey] || {
          stroke: CHART_LINE_COLOR,
          warn: '#ffa65c',
          crit: '#ff3d57',
        }
      : { stroke: CHART_LINE_COLOR, warn: '#ffa65c', crit: '#ff3d57' }
    const slug = sensor.slug ?? sensor.name
    const liveRows = datalogsBySensor[slug]
    const liveSeries = liveRows ? mapDatalogsToSeries(liveRows) : []
    const sensorKeys = [sensor.id, sensor.sensorId, sensor.uuid].filter(
      (k) => k !== null && k !== undefined
    )
    let apiBands = []
    for (const k of sensorKeys) {
      if (alarmBandsBySensorId?.[k]) {
        apiBands = alarmBandsBySensorId[k]
        break
      }
    }
    const bands = Array.isArray(apiBands) ? apiBands : []
    return {
      key: sensor.uuid ?? `${sensor.name ?? 'sensor'}-${idx}`,
      label: sensor.unit ? `${sensor.name} (${sensor.unit})` : sensor.name,
      series: liveSeries,
      palette,
      unit: sensor.unit,
      bands,
    }
  })
}

// Per-sensor SVG chart. Mirrors DeviceDetail's SensorChart so hover
// behaviour, Y-axis labels, threshold bands, and tooltip all match the
// device page exactly.
const SensorChart = ({ series, palette, unit, bands = [] }) => {
  const [hoverIdx, setHoverIdx] = useState(null)
  const [hoverX, setHoverX] = useState(null)
  if (!Array.isArray(series) || series.length === 0) {
    return (
      <div className="sensor-chart sensor-chart--empty">
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none">
          {GRID_Y.map((y) => (
            <line key={y} x1="0" y1={y} x2={CHART_W} y2={y} className="sensor-chart__grid" />
          ))}
        </svg>
        <div className="sensor-chart__empty-overlay">No data found</div>
      </div>
    )
  }
  const { toY, toValue } = buildScaler(series, bands)
  const thresholdLines = []
  bands.forEach((band, bandIdx) => {
    const entries = [
      { value: band?.warnUpper, color: palette.warn, kind: 'warn-upper', dash: '6 4' },
      { value: band?.warnLower, color: palette.warn, kind: 'warn-lower', dash: '6 4' },
      { value: band?.critUpper, color: palette.crit, kind: 'crit-upper', dash: '4 2' },
      { value: band?.critLower, color: palette.crit, kind: 'crit-lower', dash: '4 2' },
    ]
    for (const e of entries) {
      if (e.value === null || e.value === undefined) continue
      thresholdLines.push({ ...e, key: `b${bandIdx}-${e.kind}-${e.value}` })
    }
  })
  const line = `M ${series
    .map((d, i) => `${(i * CHART_W) / (series.length - 1)},${toY(d.v)}`)
    .join(' L ')}`
  const xs = series.map((_, i) => (i * CHART_W) / (series.length - 1))
  const snapToIndex = (x) => {
    if (!Number.isFinite(x) || xs.length === 0) return null
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < xs.length; i += 1) {
      const d = Math.abs(xs[i] - x)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    return bestIdx
  }
  const onSvgMove = (e) => {
    const svg = e.currentTarget
    const rect = svg.getBoundingClientRect()
    const xRatio = CHART_W / rect.width
    const x = (e.clientX - rect.left) * xRatio
    const idx = snapToIndex(x)
    setHoverIdx(idx)
    setHoverX(idx === null ? null : xs[idx])
  }
  const onSvgLeave = () => {
    setHoverIdx(null)
    setHoverX(null)
  }
  const hoverPoint = hoverIdx !== null && series[hoverIdx] ? series[hoverIdx] : null
  const hoverY = hoverPoint ? toY(hoverPoint.v) : null
  const tooltipLeftPx = '8px'
  const tooltipTopPx = '28px'
  void hoverX
  void hoverY

  return (
    <div className="sensor-chart">
      <div className="sensor-chart__y-axis">
        {GRID_Y.map((y) => (
          <span
            key={`yt-${y}`}
            className="sensor-chart__y-label"
            style={{ top: `${(y / CHART_H) * 100}%` }}
          >
            {formatTickValue(toValue(y))}
          </span>
        ))}
      </div>
      <svg
        className="sensor-chart__svg"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        preserveAspectRatio="none"
        onMouseMove={onSvgMove}
        onMouseLeave={onSvgLeave}
      >
        {GRID_Y.map((y) => (
          <line key={y} x1="0" y1={y} x2={CHART_W} y2={y} className="sensor-chart__grid" />
        ))}
        <path d={line} fill="none" stroke={palette.stroke} strokeWidth="2" />
        {thresholdLines.map((t) => (
          <line
            key={`overlay-${t.key}-${t.value}`}
            x1="0"
            y1={toY(t.value)}
            x2={CHART_W}
            y2={toY(t.value)}
            stroke={t.color}
            strokeDasharray={t.dash}
            strokeWidth="1.6"
            strokeOpacity="0.95"
          />
        ))}
        {xs.map((x, i) => (
          <circle
            key={`hit-${i}`}
            cx={x}
            cy={toY(series[i].v)}
            r={12}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
          />
        ))}
        {hoverX !== null && hoverY !== null && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              y1={0}
              x2={hoverX}
              y2={CHART_H}
              stroke={palette.stroke}
              strokeOpacity="0.45"
              strokeDasharray="3 3"
              strokeWidth="1"
            />
            <circle
              cx={hoverX}
              cy={hoverY}
              r={4.5}
              fill={palette.stroke}
              stroke="#fff"
              strokeWidth="1.5"
            />
          </g>
        )}
      </svg>
      {hoverPoint && tooltipLeftPx !== null && tooltipTopPx !== null && (
        <div
          className="sensor-chart__tooltip"
          style={{ left: tooltipLeftPx, top: tooltipTopPx }}
          role="tooltip"
          aria-live="polite"
        >
          <div className="sensor-chart__tooltip-value">
            <span style={{ color: palette.stroke }}>
              {Number.isFinite(hoverPoint.v) ? hoverPoint.v : '--'}
            </span>
            <span className="sensor-chart__tooltip-unit">{unit || ''}</span>
          </div>
          <div className="sensor-chart__tooltip-time">
            {hoverPoint.date ? `${hoverPoint.date} · ${hoverPoint.time}` : hoverPoint.time}
          </div>
        </div>
      )}
      <span className="sensor-chart__corner">{`24h · ${unit || ''}`}</span>
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────
export default function InputModuleDetail() {
  // Re-render this page when the admin changes the Global Settings
  // timezone, so every timestamp below re-formats immediately.
  useTimezone()
  const { uuid } = useParams()
  const navigate = useNavigate()

  // Fetch the input-module details from /input-modules/{uuid}. The
  // response body is wrapped in `{ success, message, data }`; the module
  // record lives at `data`. While the request is in flight (or fails)
  // we fall back to `DUMMY_INPUT_MODULE` so the layout / labels still
  // render — the user's selection (the row they clicked) drives which
  // uuid we fetch for.
  const { data: inputModuleResponse } = useQuery({
    queryKey: ['input-module', uuid],
    queryFn: () => getInputModuleByUuidApi(uuid),
    enabled: Boolean(uuid),
    refetchOnMount: 'always',
  })
  const apiModule = inputModuleResponse?.data?.data ?? inputModuleResponse?.data ?? null
  // API takes precedence over the dummy. While loading (apiModule is
  // null), the page renders with the dummy values so the layout never
  // collapses.
  const inputModule = apiModule

  // Derive current-reading cards from `inputModule.lastLogData`. Same
  // shape as DeviceDetail so the two pages render identically.
  const currentReadings = buildCurrentReadings(inputModule?.lastLogData)

  // Parallel fetch: one /alarm-profiles/{uuid} call per template the
  // module reports. Mirrors DeviceDetail so the two pages render
  // identically — the template's `uuid` resolves the band data; the
  // `useQueries` call lets every template resolve independently so one
  // slow profile doesn't block the rest.
  const alarmTemplates = Array.isArray(inputModule?.alarmProfileTemplates)
    ? inputModule.alarmProfileTemplates
    : []
  const alarmProfileQueries = useQueries({
    queries: alarmTemplates.map((tpl) => ({
      queryKey: ['alarm-profile', tpl?.uuid],
      queryFn: () => getAlarmProfileByUuid(tpl?.uuid),
      enabled: Boolean(tpl?.uuid),
      staleTime: 60_000,
    })),
  })

  // Group resolved profiles by template so a single template with both
  // WARNING and CRITICAL bands renders as one card containing both
  // severity sub-rows. Same shape as DeviceDetail's `alarmGroups`.
  const alarmGroups = useMemo(() => {
    return alarmTemplates
      .map((tpl, idx) => {
        const data = alarmProfileQueries[idx]?.data?.data?.data
        if (!data) return null
        const bands = Array.isArray(data.profiles) ? data.profiles : []
        const bySeverity = {}
        bands.forEach((band) => {
          const sev = (band?.severity || '').toUpperCase()
          if (!sev) return
          bySeverity[sev] = band
        })
        return {
          uuid: tpl?.uuid,
          profileName: data.name || tpl?.name || 'Alarm profile',
          status: data.status,
          warn: bySeverity.WARNING,
          crit: bySeverity.CRITICAL,
        }
      })
      .filter(Boolean)
  }, [alarmTemplates, alarmProfileQueries])

  // 24h window for chart fetches. Anchored at mount time so the datalog
  // query shares the same `startDate`/`endDate` window. Refresh on
  // remount via the `enabled` flag + `staleTime` (see query config
  // below).
  const chartWindow = useMemo(() => {
    const endDate = new Date()
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000)
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
  }, [])

  // Build a `sensors` array from the module's single `sensor` field so
  // the shared `buildSensorCharts` (same shape as DeviceDetail) can
  // produce one chart descriptor per measurement. Empty array when the
  // API hasn't returned a sensor — the chart then renders its "No data
  // found" empty state.
  const moduleSensors = useMemo(() => {
    const s = inputModule?.sensor
    if (!s || (!s.name && !s.uuid)) return []
    return [{ ...s, slug: s.slug ?? s.name }]
  }, [inputModule?.sensor])

  // Parallel fetch: one /device-datalogs/filter call per reported sensor.
  // Reuses the device-page endpoint with the module's `serialNumber` and
  // the sensor name as the slug; an empty payload falls through to the
  // "No data found" empty state in `SensorChart`.
  const moduleSerial = inputModule?.serialNumber
  const datalogQueries = useQueries({
    queries: moduleSensors.map((sensor) => ({
      queryKey: [
        'device-datalogs',
        moduleSerial,
        sensor.slug ?? sensor.name,
        chartWindow.startDate,
        chartWindow.endDate,
      ],
      queryFn: () =>
        filterDeviceDatalogsApi({
          serialNumber: moduleSerial,
          sensorSlug: sensor.slug ?? sensor.name,
          startDate: chartWindow.startDate,
          endDate: chartWindow.endDate,
        }),
      enabled: Boolean(moduleSerial) && Boolean(sensor.slug ?? sensor.name),
      staleTime: 60_000,
    })),
  })
  const datalogsBySensor = moduleSensors.reduce((acc, sensor, i) => {
    const slug = sensor.slug ?? sensor.name
    const response = datalogQueries[i]?.data
    const rows = response?.data?.data?.datalog ?? []
    acc[slug] = Array.isArray(rows) ? rows : []
    return acc
  }, {})

  // Extract threshold bands from the same datalog payload the device
  // page reads. Mirrors DeviceDetail's `alarmBandsBySensorId` so the
  // chart renders the same dashed tolerance lines as the device view.
  const alarmBandsBySensorId = useMemo(() => {
    const source = datalogQueries.find(
      (q) =>
        Array.isArray(q?.data?.data?.data?.alarmProfile) && q.data.data.data.alarmProfile.length > 0
    )
    const profiles = source?.data?.data?.data?.alarmProfile
    if (!Array.isArray(profiles) || profiles.length === 0) return {}
    return profiles.reduce((acc, profile) => {
      const bands = bandsFromAlarmProfile(profile?.profiles)
      const pushInto = (key) => {
        if (key == null) return
        if (!acc[key]) acc[key] = []
        acc[key].push({ ...bands, name: profile?.name || '' })
      }
      pushInto(profile?.sensorId)
      pushInto(profile?.uuid)
      return acc
    }, {})
  }, [datalogQueries])

  // One chart descriptor per sensor. Same shape as DeviceDetail so the
  // JSX can drop the result straight into the shared `SensorChart`.
  const sensorCharts = buildSensorCharts(moduleSensors, datalogsBySensor, alarmBandsBySensorId)

  const [publicView, setPublicView] = useState(false)

  const validityDays = diffFromNow(inputModule?.calibrationExpiryDate, 'days')

  const validityCell =
    validityDays === null ? (
      ValidityCell
    ) : validityDays >= 0 ? (
      <StatusBadge color="green" dot>
        {`Valid · ${validityDays} days`}
      </StatusBadge>
    ) : (
      <StatusBadge color="red" dot>
        {`Expired ${Math.abs(validityDays)} days ago`}
      </StatusBadge>
    )

  const calibrationRows = [
    { label: 'Type', value: 'Accredited (ISO 17025)' },
    { label: 'Calibration date', value: '16 Aug 2024' },
    { label: 'Expire date', value: formatDateOnly(inputModule?.calibrationExpiryDate) || '--' },
    { label: 'Validity', value: validityCell },
  ]

  // Parameters cell renders the attached sensor's name + unit as a chip
  // so the user can confirm at a glance which parameter this module is
  // recording against.
  const parametersCell = inputModule?.sensor ? (
    <div className="flex gap-1 flex-wrap">
      <StatusBadge color="default">
        {inputModule.sensor.unit
          ? `${inputModule.sensor.name} ${inputModule.sensor.unit}`
          : inputModule.sensor.name}
      </StatusBadge>
    </div>
  ) : (
    'no sensors found'
  )

  // Display the V-Tag's user-facing id (e.g. "VT-00013"). The API
  // returns it as a nested object; the `name` and `assetTagSerial` fields
  // both carry the same display string in practice, but `assetTagSerial`
  // is the canonical wire-format value.
  const vTagDisplay = inputModule?.vTag?.assetTagSerial ?? inputModule?.vTag?.name ?? '--'

  const identificationRows = [
    { label: 'Model number', value: inputModule?.modelNumber ?? '--' },
    { label: 'Module name', value: inputModule?.name ?? '--' },
    { label: 'Module serial', value: inputModule?.serialNumber ?? '--' },
    { label: 'Input type', value: inputModule?.inputType?.name ?? '--' },
    // { label: 'Multi-use module', value: inputModule?.multiUse ?? 'Yes' },
    // { label: 'Reporting interval', value: inputModule?.reportingInterval ?? '10 minutes' },
    // { label: 'Start delay', value: inputModule?.startDelay ?? '5 minutes' },
    { label: 'V-Tag', value: vTagDisplay },
    {
      label: 'Connected device',
      value: inputModule?.attachWithDevice?.serialNumber ?? '--',
    },
    { label: 'File upload', value: 'Upload calibration certificate' },
    { label: 'Expire date', value: formatDateOnly(inputModule?.calibrationExpiryDate) || '--' },
    { label: 'Brand / vendor', value: inputModule?.manufacturer?.name ?? '--' },
    { label: 'Parameters', value: parametersCell },
  ]

  return (
    <div className="sensor-page">
      {/* Top Bar */}
      <div className="sensor-topbar">
        <div>
          {/* Sits above the breadcrumb so the user can return to the
              input-modules listing page without using the browser's back
              button. Uses navigate(-1) so the listing's scroll position,
              search input, and active filters are preserved. */}
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 1.5,
              ml: -1,
              textTransform: 'none',
              // Subtle primary-tinted rectangle on hover, darkening to a
              // deeper shade so the click target reads as a square button.
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.24)',
              },
            }}
          >
            Back
          </Button>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link underline="hover" color="inherit" href="/devices">
              Devices
            </Link>
            <p>Input Modules</p>
            <Typography variant="body2" color="text.primary">
              {inputModule?.name ?? 'IM-Temp-Humidity-01'}
            </Typography>
          </Breadcrumbs>
          <div className="flex items-center gap-3 mb-1">
            <Typography variant="h5">{inputModule?.name ?? 'IM-Temp-Humidity-01'}</Typography>
            <StatusBadge color="green">● Online</StatusBadge>
            <StatusBadge color="blue">Pharma</StatusBadge>
          </div>
          <Typography className="text-gray-400">
            {inputModule?.manufacturer?.name ?? '--'} · {inputModule?.inputType?.name ?? 'Input'}{' '}
            input · serial {inputModule?.serialNumber ?? '--'} ·{' '}
            {inputModule?.facility?.name ?? '--'} · {inputModule?.location ?? '--'}
          </Typography>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outlined" startIcon={<TrendingUp size={14} />}>
            Full trends
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="sensor-body">
        {/* LEFT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Current Readings — one card per `lastLogData` key. Mirrors
              the DeviceDetail layout so the two detail pages read
              identically. The SectionHeader's `action` slot carries the
              snapshot timestamp from `inputModule.lastLogTime`. */}
          <Card className="px-4 py-3">
            <SectionHeader
              icon={Settings}
              label="Current Readings"
              action={
                inputModule?.lastLogTime ? (
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(inputModule.lastLogTime)}
                  </Typography>
                ) : null
              }
            />
            {currentReadings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No current readings available
              </Typography>
            ) : (
              <div className="sensor-readings">
                {currentReadings.map((reading) => (
                  <div
                    key={reading.key}
                    className="sensor-reading-card"
                    title={`${reading.label}${reading.unit ? ` (${reading.unit})` : ''}`}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {reading.label}
                    </Typography>
                    <div className="sensor-reading-card__value">
                      <span className="sensor-reading-card__number">{reading.value ?? '--'}</span>
                      {reading.unit ? (
                        <span className="sensor-reading-card__unit">{reading.unit}</span>
                      ) : null}
                    </div>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(inputModule?.lastLogTime) || '--'}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* 24-Hour Trend — one chart per sensor reported by the module.
              Mirrors DeviceDetail's layout so both detail pages render
              the same trend experience (one card per sensor with
              tolerance bands, hover tooltip, crosshair, and Y-axis
              labels). */}
          <Card className="p-5">
            <SectionHeader
              icon={TrendingUp}
              label={`24-Hour Trend${sensorCharts.length > 1 ? 's' : ''}`}
              action={<span className="section-header__hint">Tolerance band shown</span>}
            />
            <div className="sensor-legend">
              <LegendItem color="#ff3d57" label="Critical" />
              <LegendItem color="#ffa65c" label="Warning" />
            </div>
            <div className="flex flex-col gap-4">
              {sensorCharts.map((chart) => (
                <div key={chart.key}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, color: 'var(--text-primary)' }}
                  >
                    {chart.label}
                  </Typography>
                  <SensorChart
                    series={chart.series}
                    palette={chart.palette}
                    unit={chart.unit}
                    bands={chart.bands}
                  />
                </div>
              ))}
            </div>
          </Card>

          {/* Alarm Profiles — driven by `alarmProfileTemplates` so the
              card renders whatever the backend actually attached to this
              module. Mirrors DeviceDetail: each template resolves to one
              card with WARNING + CRITICAL band rows; a "Manage" button
              routes to the alarm details page when the profile is known. */}
          <Card className="p-5">
            <SectionHeader
              icon={Bell}
              label={
                <>
                  Alarm Profiles{' '}
                  <span className="section-header__count">
                    · {alarmGroups.length} of {alarmTemplates.length || 0}
                  </span>{' '}
                </>
              }
            />
            <div className="sensor-alarm-list">
              {alarmGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No alarm profiles attached
                </Typography>
              ) : (
                alarmGroups.map((group) => {
                  // Render helper: turns a single severity band into the
                  // (label+icon, range string) tuple used by both WARNING
                  // and CRITICAL rows inside the group card.
                  const bandRow = (band, sev) => {
                    if (!band) return null
                    const min = band.minValue
                    const max = band.maxValue
                    const minNum = Number(min)
                    const maxNum = Number(max)
                    const minFinite = Number.isFinite(minNum)
                    const maxFinite = Number.isFinite(maxNum)
                    const rangeText =
                      minFinite && maxFinite
                        ? minNum === maxNum
                          ? `= ${min}`
                          : `${min} – ${max}`
                        : maxFinite
                          ? `> ${max}`
                          : minFinite
                            ? `< ${min}`
                            : ''
                    const delayText = band.alarmDelay ? `delay ${band.alarmDelay} min` : ''
                    const isCrit = sev === 'CRITICAL'
                    const iconClass = isCrit ? 'sensor-alarm--critical' : 'sensor-alarm--warning'
                    return (
                      <div
                        key={`${group.uuid}-${sev}`}
                        className={`sensor-alarm__band sensor-alarm__band--${isCrit ? 'critical' : 'warning'}`}
                      >
                        <span className={`sensor-alarm__icon ${iconClass}`}>
                          <Bell size={16} strokeWidth={2.5} />
                        </span>
                        <span className="sensor-alarm__band-label">{sev}</span>
                        <span className="sensor-alarm__band-text">
                          {rangeText}
                          {rangeText && delayText ? ' · ' : ''}
                          {delayText}
                        </span>
                      </div>
                    )
                  }
                  const isActive = (group.status || '').toUpperCase() === 'ACTIVE'
                  return (
                    <div key={group.uuid} className="sensor-alarm sensor-alarm--group">
                      <div className="sensor-alarm__left">
                        <span
                          className={`sensor-alarm__icon ${
                            isActive ? 'sensor-alarm--active' : 'sensor-alarm--critical'
                          }`}
                        >
                          <Bell size={18} />
                        </span>
                        <div>
                          <Typography variant="subtitle2" fontWeight={700}>
                            {group.profileName}
                          </Typography>
                          <div className="sensor-alarm__bands">
                            {bandRow(group.warn, 'WARNING')}
                            {bandRow(group.crit, 'CRITICAL')}
                          </div>
                        </div>
                      </div>
                      <div className="sensor-alarm__right">
                        <StatusBadge color={isActive ? 'green' : 'red'} dot>
                          {group.status || 'Inactive'}
                        </StatusBadge>
                        <Button
                          size="small"
                          variant="outlined"
                          className="sensor-btn-tiny"
                          onClick={() => navigate(`/alarms/details/${group.uuid}`)}
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </Card>

          {/* V-Tag Swap History — same data-driven table as the Device
              detail page, keyed on the input module's asset-tag uuid.
              Until the by-uuid payload lands `assetTag` data, the table
              renders its "No asset tag associated with this device"
              empty state (gated by `Boolean(uuid)` inside the component). */}
          <Card className="p-5">
            <SectionHeader icon={Tag} label={`V-Tag Swap History — ${vTagDisplay}`} />
            <AssetTagHistoryTable uuid={inputModule?.vTag?.uuid} />
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="flex flex-col gap-5">
          {/* Identification */}
          <Card className="p-5">
            <SectionHeader icon={IdCard} label="Identification" />
            {identificationRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
          </Card>

          {/* Calibration */}
          <Card className="p-5">
            <SectionHeader icon={Settings} label="Calibration" />
            {calibrationRows.map((row) => (
              <InfoRow key={row.label} {...row} />
            ))}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download size={14} />}
              sx={{ marginTop: '12px' }}
              component="a"
              href={inputModule?.calibrationCertificateUrl || undefined}
              target="_blank"
              rel="noopener noreferrer"
              download
              disabled={!inputModule?.calibrationCertificateUrl}
            >
              Download certificate PDF
            </Button>
          </Card>

          {/* Module Health */}
          <Card className="p-5">
            <SectionHeader icon={Activity} label="Module Health" />
            <div className="flex flex-col">
              {HEALTH_ROWS.map((row) => (
                <InfoRow key={row.label} {...row} />
              ))}
            </div>
          </Card>

          {/* Location */}
          <Card className="p-5">
            <SectionHeader icon={MapPin} label="Location & Public View" />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Location note
            </Typography>
            <div className="sensor-location">
              <MapPin size={14} />
              <Typography variant="body1">Cold Room 2, top shelf unit point</Typography>
            </div>
            <div className="mt-3">
              {/* Header row */}
              <Box className="flex items-start justify-between">
                <Box>
                  <Box className="flex items-baseline gap-2">
                    <Typography variant="body2" fontWeight={600}>
                      Public view-only URL
                    </Typography>
                    <Box className="text-blue-400">
                      <ExternalLink size={14} />
                    </Box>
                  </Box>
                  <Typography variant="body1" fontWeight={400}>
                    No-login QR page · {publicView ? 'enabled' : 'disabled'}
                  </Typography>
                </Box>

                {/* Toggle */}
                <Switch
                  checked={publicView}
                  onChange={(e) => setPublicView(e.target.checked)}
                  sx={{ mt: 0.3 }}
                />
              </Box>

              {/* Body row */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mt: 2 }}>
                {/* Dummy QR image from public API */}
                <Box className="qr-container">
                  <img
                    src="https://api.qrserver.com/v1/create-qr-code/?size=88x88&data=https://example.com/input-module/public"
                    alt="QR Code"
                    width={88}
                    height={88}
                    style={{ display: 'block' }}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary">
                  Enable to generate a QR code that shows this module's current data on a public
                  page.
                </Typography>
              </Box>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
